import ApiRoutes from '@modules/account/defs/api-routes';
import {
  AccountInfo,
  UpdateAccountInfoInput,
  UpdatePasswordInput,
} from '@modules/account/defs/types';
import useApi, { ApiResponse, FetchApiOptions } from '@common/hooks/useApi';
import useSWR from 'swr';
import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { Booking } from '@modules/leads/defs/types';
import useAuth from '@modules/auth/hooks/api/useAuth';

const useAccount = () => {
  const fetchApi = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const { data, error, mutate } = useSWR<AccountInfo | null>(
    user ? ApiRoutes.GetAccountInfo : null,
    async (url: string): Promise<AccountInfo | null> => {
      const response = await fetchApi<AccountInfo>(url, {
        method: 'GET',
      });

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const updateAccountInfo = useCallback(
    async (accountInfo: UpdateAccountInfoInput, options?: FetchApiOptions) => {
      const response = await fetchApi<AccountInfo>(ApiRoutes.UpdateAccountInfo, {
        method: 'PUT',
        data: accountInfo,
        ...options,
      });

      if (response.success && response.data) {
        await mutate();
        if (response.message) {
          enqueueSnackbar(response.message, { variant: 'success' });
        }
        return {
          success: response.success,
          data: response.data,
          errors: response.errors,
        };
      }

      return {
        success: response.success,
        errors: response.errors,
      };
    },
    [fetchApi, mutate, enqueueSnackbar]
  );

  const updatePassword = useCallback(
    async (passwordData: UpdatePasswordInput, options?: FetchApiOptions) => {
      const response = await fetchApi<{ message: string }>(ApiRoutes.UpdatePassword, {
        method: 'PUT',
        data: passwordData,
        ...options,
      });

      if (response.success && response.message) {
        enqueueSnackbar(response.message, { variant: 'success' });
      }

      return {
        success: response.success,
        message: response.data?.message,
        errors: response.errors,
      };
    },
    [fetchApi, enqueueSnackbar]
  );

  const readMyBookings = useCallback(
    async (options?: FetchApiOptions) => {
      const response = await fetchApi<{ items: Booking[] }>(ApiRoutes.ReadMyBookings, {
        method: 'GET',
        ...options,
      });
      return response;
    },
    [fetchApi]
  );

  const updatePushSubscription = useCallback(
    async (
      subscription: PushSubscription,
      options?: FetchApiOptions
    ): Promise<ApiResponse<null>> => {
      const response = await fetchApi<null>(ApiRoutes.UpdatePushSubscription, {
        method: 'POST',
        data: subscription,
        ...options,
      });

      return response;
    },
    [fetchApi]
  );

  return {
    data: data ?? null,
    isLoading: !error && data === undefined,
    error,
    mutate,
    updateAccountInfo,
    updatePassword,
    readMyBookings,
    updatePushSubscription,
  };
};

export default useAccount;
